import React, { useEffect, useRef } from 'react';
import { Cable } from 'lucide-react';
import integrationImage from '../assets/integration.png';

const Integrations = () => {
  const imageRef = useRef<HTMLImageElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Image scale animation observer
    const image = imageRef.current;
    const section = sectionRef.current;
    if (!image || !section) return;

    let maxScaleReached = false;

    const scaleObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const ratio = entry.intersectionRatio;
            if (!maxScaleReached) {
              const scale = Math.min(1.2, 1.05 + (ratio * 0.15));
              image.style.transform = `scale(${scale})`;
              if (scale >= 1.2) {
                maxScaleReached = true;
              }
            }
          }
        });
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
        rootMargin: '-100px 0px -100px 0px'
      }
    );

    // Fade up animation observer with earlier trigger
    const fadeUpObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      {
        rootMargin: '300px 0px -300px 0px',
        threshold: 0.1
      }
    );

    scaleObserver.observe(section);

    // Observe all fade-up elements
    const fadeElements = titleRef.current?.querySelectorAll('.fade-up-on-scroll');
    fadeElements?.forEach(element => {
      fadeUpObserver.observe(element);
    });

    return () => {
      scaleObserver.disconnect();
      fadeUpObserver.disconnect();
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative w-full pt-16 pb-0 overflow-visible -mt-27">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex justify-between items-start">
          <div ref={titleRef}>
            <div className="flex items-center gap-2 opacity-0 fade-up-on-scroll">
              <Cable className="w-4 h-4 text-gray-300 filter drop-shadow-[0_0_2px_rgba(255,255,255,0.6)] mb-3" />
              <div className="text-sm text-gray-400 font-['VCR_OSD_Mono'] font-normal mb-3">INTEGRATIONS</div>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight font-sans z-index-40 opacity-0 fade-up-on-scroll">
              Don't let go of your favorite tools.
            </h2>
            <p className="mt-3 text-lg text-gray-400 font-sans opacity-0 fade-up-on-scroll">
              We understand every team is unique. Automatically connect to the tech-stack your team is already using.
            </p>
          </div>
        </div>

        <div className="relative mt-16 flex justify-center">
          <div className="relative w-full max-w-5xl">
            {/* Left glow effect */}
            <div className="absolute inset-0 overflow-visible pointer-events-none z-30">
              <div
                className="absolute top-[10%] left-[30%]
                  w-[750px] h-[600px]
                  rounded-full opacity-[0.30]
                  pointer-events-none"
                  style={{
                    background: `
                      radial-gradient(circle at 50% 50%,
                        rgba(255,110,199,0.30)  0%,   /* soft pink */
                        rgba(232, 80, 185,0.26) 10%,   /* pink-magenta */
                        rgba(209, 50, 168,0.22) 25%,   /* magenta */
                        rgba(186, 25, 150,0.18) 40%,   /* deep magenta */
                        rgba(160,  5, 132,0.14) 55%,   /* purple */
                        rgba(120,  0, 100,0.08) 75%,   /* dark purple */
                        rgba(  9, 16, 28,0.04) 90%,   /* almost bg */
                        transparent              80%  /* fully gone */
                      )
                    `,
                    filter: 'blur(20px)',
                  }}
              />
            </div>

            {/* Right glow effect */}
            <div className="absolute top-0 right-0 w-full h-full overflow-visible pointer-events-none z-30">
              <div
                className="absolute top-[-15%] right-[65%]
                          w-[500px] h-[370px]
                          rounded-full animate-pulse
                          pointer-events-none"
                style={{
                  animationDelay: '2s',
                  background: `
                    radial-gradient(circle at 100% 0%,
                      rgba(168,  85, 247, 0.25)  0%,
                      rgba(236,  72, 153, 0.19) 30%,
                      rgba(168,  85, 247, 0.10) 60%,
                      rgba(  9,  16,  28, 0.03) 80%,
                      transparent               100%
                    )
                  `,
                  filter: 'blur(60px)',
                }}
              />
            </div>

            <img
              ref={imageRef}
              src={integrationImage}
              alt="AI Integrations"
              className="w-full h-auto scale-105 relative z-10 transition-transform duration-300 ease-out"
              style={{ transform: 'scale(1.05)' }}
            />
            
            <div className="absolute bottom-0 left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen h-[67.5%] bg-gradient-to-t from-[#070d17] via-[#070d17] to-transparent z-20" />
            <div className="absolute top-full left-[-5vw] w-screen h-[600px] bg-[#070d17] z-20" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Integrations; 